import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Loader2, Sparkles } from "lucide-react";

function GoogleButton({ label = "Mit Google anmelden" }) {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };
  return (
    <button
      type="button"
      onClick={handleGoogle}
      data-testid="google-auth-btn"
      className="w-full inline-flex items-center justify-center gap-2.5 rounded-lg border border-[#EAEBE6] bg-white hover:bg-[#F4F5F2] text-[#272525] font-semibold py-3 transition-colors"
    >
      <svg className="h-5 w-5" viewBox="0 0 48 48" aria-hidden>
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.8 32.5 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
        <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.7 15 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.1z"/>
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.8-3.4-11.3-8.1l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2C41.5 35.8 44 30.3 44 24c0-1.2-.1-2.3-.4-3.5z"/>
      </svg>
      {label}
    </button>
  );
}

function AuthShell({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2" data-testid="auth-shell">
      <div className="flex items-center justify-center p-8 lg:p-12 bg-[#FCFCFB]">
        <div className="w-full max-w-md">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-[#FCEBD6] text-[#B36B15] tracking-wide">
            <Sparkles className="h-3.5 w-3.5" /> Alpha · Early Tester
          </div>
          <h1 className="mt-5 text-3xl sm:text-4xl font-semibold text-[#5A5B5C] tracking-tight">
            {title}
          </h1>
          <p className="mt-2 text-[#272525]">{subtitle}</p>
          <div className="mt-8">{children}</div>
          <div className="mt-6 text-sm text-[#5A5B5C]">{footer}</div>
        </div>
      </div>
      <div className="hidden lg:block relative bg-[#F4F5F2] overflow-hidden">
        <img
          src="https://images.pexels.com/photos/4267531/pexels-photo-4267531.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
          alt="KI-Leitstelle Training"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E1F1A]/75 via-[#1E1F1A]/30 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white max-w-md">
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#A7B584]">
            Help4Emergency · KI-Leitstelle
          </div>
          <h2 className="mt-3 text-3xl font-semibold leading-tight">
            Wer geübt hat, handelt im Ernstfall ruhiger.
          </h2>
          <p className="mt-3 text-white/80 leading-relaxed">
            Realistische 112/110-Simulation per Sprache — mit sofortigem Feedback und
            Trainer-Dashboard.
          </p>
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      const from = location.state?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Willkommen zurück."
      subtitle="Melden Sie sich an, um die KI-Leitstelle zu testen."
      footer={
        <>
          Noch keinen Account?{" "}
          <Link to="/register" className="text-[#A7B584] hover:text-[#8C9A69] font-semibold" data-testid="login-to-register">
            Early-Tester werden
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="email" className="text-[#5A5B5C] text-sm font-semibold">E-Mail</Label>
          <Input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email-input"
            className="mt-1.5 h-11 border-[#EAEBE6] rounded-lg focus-visible:ring-[#A7B584] focus-visible:border-[#A7B584]"
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-[#5A5B5C] text-sm font-semibold">Passwort</Label>
            <Link
              to="/passwort-vergessen"
              className="text-xs text-[#A7B584] hover:text-[#8C9A69] font-semibold"
              data-testid="login-forgot-link"
            >
              Passwort vergessen?
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password-input"
            className="mt-1.5 h-11 border-[#EAEBE6] rounded-lg focus-visible:ring-[#A7B584] focus-visible:border-[#A7B584]"
          />
        </div>

        {err && (
          <div className="text-sm rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2" data-testid="login-error">
            {err}
          </div>
        )}

        <Button
          type="submit"
          disabled={loading}
          data-testid="login-submit-btn"
          className="w-full bg-[#A7B584] hover:bg-[#8C9A69] text-white rounded-lg h-11 font-semibold"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Anmelden <ArrowRight className="ml-1.5 h-4 w-4" /></>}
        </Button>
      </form>

      <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-[#5A5B5C]">
        <span className="flex-1 h-px bg-[#EAEBE6]" />
        oder
        <span className="flex-1 h-px bg-[#EAEBE6]" />
      </div>

      <GoogleButton />
    </AuthShell>
  );
}

export function RegisterPage() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accept, setAccept] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!accept) {
      setErr("Bitte bestätigen Sie die Teilnahme am Alpha-Programm.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      setUser(data);
      navigate("/dashboard", { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Early-Tester werden."
      subtitle="Kostenfreier Alpha-Zugang zur KI-Leitstelle."
      footer={
        <>
          Bereits registriert?{" "}
          <Link to="/login" className="text-[#A7B584] hover:text-[#8C9A69] font-semibold" data-testid="register-to-login">
            Anmelden
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
        <div>
          <Label htmlFor="name" className="text-[#5A5B5C] text-sm font-semibold">Name (optional)</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="register-name-input"
            className="mt-1.5 h-11 border-[#EAEBE6] rounded-lg focus-visible:ring-[#A7B584] focus-visible:border-[#A7B584]"
          />
        </div>
        <div>
          <Label htmlFor="email" className="text-[#5A5B5C] text-sm font-semibold">E-Mail</Label>
          <Input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="register-email-input"
            className="mt-1.5 h-11 border-[#EAEBE6] rounded-lg focus-visible:ring-[#A7B584] focus-visible:border-[#A7B584]"
          />
        </div>
        <div>
          <Label htmlFor="password" className="text-[#5A5B5C] text-sm font-semibold">Passwort (min. 6 Zeichen)</Label>
          <Input
            id="password"
            type="password"
            required
            minLength={6}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="register-password-input"
            className="mt-1.5 h-11 border-[#EAEBE6] rounded-lg focus-visible:ring-[#A7B584] focus-visible:border-[#A7B584]"
          />
        </div>

        <label className="flex items-start gap-3 text-sm text-[#272525] select-none">
          <input
            type="checkbox"
            checked={accept}
            onChange={(e) => setAccept(e.target.checked)}
            data-testid="register-accept-checkbox"
            className="mt-1 h-4 w-4 accent-[#A7B584]"
          />
          <span>
            Ich verstehe, dass Help4Emergency sich im <strong>Alpha-Stand</strong> befindet
            und ich als <strong>Early Tester</strong> an der Produktverbesserung mitwirke.
          </span>
        </label>

        {err && (
          <div className="text-sm rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2" data-testid="register-error">
            {err}
          </div>
        )}

        <Button
          type="submit"
          disabled={loading}
          data-testid="register-submit-btn"
          className="w-full bg-[#A7B584] hover:bg-[#8C9A69] text-white rounded-lg h-11 font-semibold"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Account erstellen <ArrowRight className="ml-1.5 h-4 w-4" /></>}
        </Button>
      </form>

      <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-[#5A5B5C]">
        <span className="flex-1 h-px bg-[#EAEBE6]" />
        oder
        <span className="flex-1 h-px bg-[#EAEBE6]" />
      </div>

      <GoogleButton label="Mit Google registrieren" />
    </AuthShell>
  );
}
